import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, CheckCircle2, AlertCircle, Search, Phone, ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const COUNTRY_CODES = [
  { code: "+593", label: "🇪🇨 +593" },
  { code: "+57", label: "🇨🇴 +57" },
  { code: "+58", label: "🇻🇪 +58" },
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+34", label: "🇪🇸 +34" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cedula, setCedula] = useState("");
  const [validation, setValidation] = useState<any>(null);
  const [validationError, setValidationError] = useState("");
  const [validating, setValidating] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", password: "", country_code: "+593", phone: "",
    al_dia: "", auto_debit: "", accept_terms: false, accept_comms: false,
  });
  const [loading, setLoading] = useState(false);

  const validateCedula = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cedula.trim()) return;
    setValidating(true);
    setValidationError("");
    try {
      const { data } = await api.post("/auth/validate-cedula", { cedula: cedula.trim() });
      setValidation(data);
      if (data.claimed) {
        setValidationError(data.message);
        return;
      }
      if (!data.found) {
        setValidationError(data.message);
        return;
      }
      setForm((f) => ({ ...f, name: data.name }));
      setStep(2);
    } catch (err: any) {
      setValidationError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setValidating(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.al_dia || !form.auto_debit) {
      toast.error("Responde las preguntas de pagos y débito automático.");
      return;
    }
    if (!form.accept_terms) {
      toast.error("Debes aceptar las Bases y Condiciones del programa.");
      return;
    }
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        cedula: cedula.trim(),
        phone: `${form.country_code} ${form.phone}`.trim(),
        al_dia: form.al_dia === "si",
        auto_debit: form.auto_debit === "si",
        accept_terms: form.accept_terms,
        accept_comms: form.accept_comms,
      });
      toast.success("Registro recibido. Tu cuenta está en proceso de aceptación.");
      navigate("/portal");
    } catch (err: any) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6 py-10 relative">
      <div className="absolute inset-0 noise-overlay opacity-50" />
      <div className="relative w-full max-w-md">
        <Link
          to="/"
          data-testid="register-back-home-link"
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-gold-ink transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Link>
        <Link to="/" className="flex items-center justify-center mb-8">
          <BrandLogo imgClassName="h-8" tag="Contigo" />
        </Link>
        <div className="card-obsidian p-8">
          {/* stepper */}
          <div className="flex items-center gap-2 mb-6">
            {[1, 2].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "gold-gradient-bg" : "bg-slate-100"}`} />
            ))}
          </div>

          {step === 1 && (
            <>
              <h1 className="text-2xl font-serif font-medium text-slate-900 mb-1">Enrolamiento de cliente</h1>
              <p className="text-sm text-slate-500 mb-7">
                Ingresa tu número de cédula/identificación. Validaremos si ya eres cliente Armony.
              </p>
              <form onSubmit={validateCedula} className="space-y-5">
                <div>
                  <Label className="text-slate-700 text-sm">Cédula / Identificación</Label>
                  <Input
                    data-testid="id-validation-input"
                    value={cedula}
                    onChange={(e) => { setCedula(e.target.value); setValidationError(""); }}
                    placeholder="Ej: 12345678"
                    required
                    className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Ingresa el documento con el que fuiste registrado por Armony. Si no está autorizado, contáctanos para incluirte en la base.
                  </p>
                </div>
                {validationError && (
                  <div
                    data-testid="id-validation-error"
                    className="flex items-start gap-2.5 p-3.5 rounded-xl bg-red-500/10 border border-red-500/30"
                  >
                    <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 leading-relaxed">{validationError}</p>
                  </div>
                )}
                <Button
                  data-testid="id-validation-submit"
                  type="submit"
                  disabled={validating}
                  className="w-full h-11 gold-gradient-bg text-white font-semibold rounded-full hover:brightness-110"
                >
                  {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-2" /> Validar cédula</>}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              {validation && (
                <div
                  data-testid="id-validation-result"
                  className={`flex items-start gap-3 p-4 rounded-xl mb-6 border ${
                    validation.found
                      ? "bg-emerald-500/10 border-emerald-500/30"
                      : "bg-amber-500/10 border-amber-500/30"
                  }`}
                >
                  {validation.found ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className="text-sm text-slate-800">{validation.message}</p>
                    {validation.found && (
                      <p className="text-xs text-emerald-700 mt-1">
                        Se acreditarán {validation.points?.toLocaleString()} puntos históricos, y los de bienvenida cuando se apruebe tu registro.
                      </p>
                    )}
                  </div>
                </div>
              )}
              <h1 className="text-2xl font-serif font-medium text-slate-900 mb-1">Completa tu registro</h1>
              <p className="text-sm text-slate-500 mb-7">Verifica tus datos y acepta las condiciones.</p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label className="text-slate-700 text-sm">Nombre completo del titular*</Label>
                  <Input
                    data-testid="register-name-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Cédula de identidad*</Label>
                  <Input
                    data-testid="register-cedula-input"
                    value={cedula}
                    disabled
                    className="mt-1.5 bg-slate-100 border-slate-200 text-slate-700 font-medium h-11"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Teléfono de contacto*</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Select value={form.country_code} onValueChange={(v) => setForm({ ...form, country_code: v })}>
                      <SelectTrigger data-testid="register-country-code" className="w-28 bg-obsidian border-slate-200 text-slate-900 h-11 shrink-0"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                        {COUNTRY_CODES.map((c) => (<SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1">
                      <Phone className="w-4 h-4 text-slate-500 absolute right-3 top-1/2 -translate-y-1/2" />
                      <Input
                        data-testid="register-phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        required
                        placeholder="321 1234567"
                        className="bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11 pr-9"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Correo electrónico*</Label>
                  <Input
                    data-testid="register-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">Contraseña*</Label>
                  <PasswordInput
                    data-testid="register-password-input"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Mínimo 6 caracteres"
                    className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 focus:border-gold h-11"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">¿Estoy al día en mis pagos?*</Label>
                  <Select value={form.al_dia} onValueChange={(v) => setForm({ ...form, al_dia: v })}>
                    <SelectTrigger data-testid="register-aldia-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 h-11"><SelectValue placeholder="Selecciona una opción" /></SelectTrigger>
                    <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                      <SelectItem value="si">Sí, estoy al día</SelectItem>
                      <SelectItem value="no">No, tengo pagos pendientes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 text-sm">¿Tu plan tiene débito automático o tarjeta de crédito activa?*</Label>
                  <Select value={form.auto_debit} onValueChange={(v) => setForm({ ...form, auto_debit: v })}>
                    <SelectTrigger data-testid="register-autodebit-select" className="mt-1.5 bg-obsidian border-slate-200 text-slate-900 h-11"><SelectValue placeholder="Selecciona una opción" /></SelectTrigger>
                    <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                      <SelectItem value="si">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="register-terms-checkbox" checked={form.accept_terms} onCheckedChange={(v) => setForm({ ...form, accept_terms: !!v })} className="mt-0.5" />
                  <span className="text-sm text-slate-700">
                    Acepto las Bases y <span className="text-gold-ink">Condiciones del programa.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="register-comms-checkbox" checked={form.accept_comms} onCheckedChange={(v) => setForm({ ...form, accept_comms: !!v })} className="mt-0.5" />
                  <span className="text-sm text-slate-700">Acepto recibir comunicaciones comerciales de Armony.</span>
                </label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="h-11 border-slate-200 text-slate-700 hover:bg-slate-100 rounded-full px-6"
                  >
                    Atrás
                  </Button>
                  <Button
                    data-testid="register-submit-button"
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-11 gold-gradient-bg text-white font-semibold rounded-full hover:brightness-110"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear cuenta"}
                  </Button>
                </div>
              </form>
            </>
          )}

          <p className="text-sm text-slate-500 text-center mt-6">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-gold-ink hover:underline">
              Ingresa
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
